import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { COMPANY_VARS } from '../utils/legal';

import logo from '../../assets/plates/nav-logo.png';

import { useInstallApp } from './app/pwaContext';

// Only the Brazilian site has an Instagram profile so far
export const INSTAGRAM_URL = 'https://www.instagram.com/usemeutreino/';
export const INSTAGRAM_HANDLE = '@usemeutreino';

// lucide dropped its brand icons: the Instagram outline, drawn in the text colour
export const InstagramGlyph = ({ className = 'mt-footer__glyph' }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <rect x="3" y="3" width="18" height="18" rx="5" />
    <circle cx="12" cy="12" r="4" />
    <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
  </svg>
);

// One footer for the whole site. "public" (landing, terms…) carries the brand; "app" is the quiet one
// at the end of the logged-in pages.
export const Footer = ({ variant = 'public' }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const navigate = useNavigate();
  const isApp = variant === 'app';
  const companyLine = t('components.Footer.company', { ...COMPANY_VARS, appName: t('appName') });
  const { canOffer, install } = useInstallApp();

  return (
    <div className="mt-root">
      <footer className={`mt-footer${isApp ? ' mt-footer--app' : ''}`} role="contentinfo">
        {!isApp && (
          <span className="mt-footer__brand">
            <img src={logo} alt="" />
            {t('appName')}
          </span>
        )}
        <nav aria-label={t('standalone.footerNav')} className="mt-footer__links">
          <button type="button" className="mt-footer__link" onClick={() => navigate('/terms')}>
            {t('standalone.agreeTermsLinkText')}
          </button>
          <button type="button" className="mt-footer__link" onClick={() => navigate('/privacy')}>
            {t('standalone.privacyLinkText')}
          </button>
          <a
            className="mt-footer__link"
            href={`mailto:${t('companyMail')}`}
            aria-label={`${t('components.Footer.contactUs')} ${t('companyMail')}`}
          >
            {t('companyMail')}
          </a>
          {language === 'pt' && (
            <a
              className="mt-footer__link mt-footer__social"
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              // The handle stays in the name so it matches what's on screen
              aria-label={`${t('components.Footer.instagram')} (${INSTAGRAM_HANDLE})`}
            >
              <InstagramGlyph />
              {INSTAGRAM_HANDLE}
            </a>
          )}
        </nav>
        {/* While this browser can install the app and it isn't running installed, on every width */}
        {canOffer && (
          <button type="button" className="mt-footer__install" onClick={() => install('footer')}>
            <img src="/apple-touch-icon.png" alt="" width="30" height="30" />
            <span>
              <span className="mt-footer__install-small">{t('components.InstallApp.badgeLine')}</span>
              <span className="mt-footer__install-big">{t('appName')}</span>
            </span>
          </button>
        )}
        <p className="mt-footer__company">{companyLine}</p>
      </footer>
    </div>
  );
};
